// Barre de progression compacte des tournées (Prompt 8 §A, maquette 5c).
// Composant partagé, informatif (non cliquable). Props : statut, dates.
import { useEffect, useState } from "react";
import { Check, PencilLine, Radio, Flag, CircleX } from "lucide-react";
import { STEP_COLORS, frDate } from "@/lib/tournee";

const STEPS = [
  ["brouillon", "Brouillon", PencilLine],
  ["active", "Active", Radio],
  ["terminee", "Terminée", Flag],
];

// Info-bulle datée par étape selon le statut courant (§A.4).
function tip(key, statut, d) {
  if (key === "brouillon") {
    return statut === "brouillon" ? `Créée le ${frDate(d.cree_le)}` : `Activée le ${frDate(d.activee_le || d.cree_le)}`;
  }
  if (key === "active") {
    if (d.figee) return `Figée depuis le ${frDate(d.figee_le || d.activee_le)}`;
    if (statut === "active") return `En cours · réponses avant le ${frDate(d.date_limite_reponse)}`;
    return statut === "brouillon" ? "À venir" : `Activée le ${frDate(d.activee_le)}`;
  }
  if (key === "terminee") {
    return statut === "terminee" ? `Clôturée le ${frDate(d.terminee_le || d.cree_le)}` : `Prévue après le ${frDate(d.periode_fin)}`;
  }
  return "";
}

export default function TourneeStepper({ statut = "brouillon", dates = {} }) {
  const [compact, setCompact] = useState(typeof window !== "undefined" && window.innerWidth < 900);
  useEffect(() => {
    const on = () => setCompact(window.innerWidth < 900);
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, []);

  const annulee = statut === "annulee";
  const curIdx = annulee ? (dates.activee_le ? 1 : 0) : STEPS.findIndex((s) => s[0] === statut);

  return (
    <div data-testid="tournee-stepper" role="list" aria-label="Progression de la tournée"
         className="flex items-center" style={{ height: 30 }}>
      {STEPS.map(([key, label, Icon], i) => {
        const passed = i < curIdx;
        const current = i === curIdx && !annulee;
        const cancelled = annulee && i === curIdx;
        const clr = STEP_COLORS[cancelled ? "annulee" : key];
        // Point
        let dotStyle, IconEl, labelStyle;
        if (cancelled) {
          dotStyle = { background: clr.dot, border: `1.5px solid ${clr.dot}` };
          IconEl = <CircleX className="w-3 h-3" style={{ color: "#fff" }} />;
          labelStyle = { color: clr.fg, fontWeight: 700 };
        } else if (passed) {
          dotStyle = { background: clr.dot, border: `1.5px solid ${clr.dot}` };
          IconEl = <Check className="w-3 h-3" style={{ color: "#fff" }} />;
          labelStyle = { color: clr.fg, fontWeight: 500 };
        } else if (current) {
          dotStyle = { background: clr.dot, border: `1.5px solid ${clr.dot}`, boxShadow: `0 0 0 3px ${clr.bg}, 0 0 0 4px ${clr.line}` };
          IconEl = <Icon className="w-3 h-3" style={{ color: "#fff" }} />;
          labelStyle = { color: clr.fg, fontWeight: 700 };
        } else {
          dotStyle = { background: "#fff", border: "1.5px solid #e6e6e1" };
          IconEl = <Icon className="w-3 h-3" style={{ color: "#9aa0ad" }} />;
          labelStyle = { color: "#9aa0ad", fontWeight: 500 };
        }
        const showLabel = current || cancelled || !compact;
        return (
          <div key={key} className="contents">
            <div role="listitem" tabIndex={0}
                 aria-current={current ? "step" : undefined}
                 aria-label={`Étape ${i + 1} sur 3 : ${cancelled ? "Annulée" : label}${current ? ", en cours" : ""}`}
                 title={cancelled ? `Annulée le ${frDate(dates.annulee_le || dates.updated_at)}${dates.annulee_motif ? " · " + dates.annulee_motif : ""}` : tip(key, statut, dates)}
                 className="flex items-center outline-none" style={{ gap: 7, padding: "0 4px", whiteSpace: "nowrap" }}>
              <span className="rounded-full flex items-center justify-center shrink-0" style={{ width: 24, height: 24, ...dotStyle }}>{IconEl}</span>
              {showLabel && <span style={{ fontSize: 13, ...labelStyle }}>{cancelled ? "Annulée" : label}</span>}
            </div>
            {i < STEPS.length - 1 && (
              <span style={{ flex: 1, minWidth: 14, height: 2, borderRadius: 2, margin: "0 6px",
                             background: i < curIdx ? STEP_COLORS[key].line : "#e6e6e1" }} />
            )}
          </div>
        );
      })}
    </div>
  );
}
